import { ref, shallowRef, toValue, type MaybeRefOrGetter } from "vue";
import { formularApi } from "@/services/api";

// Module-level cache for recruitment evaluation forms (moduleId: 4)
const recruitmentFormsCache = shallowRef<{ _id: string; display: string }[] | null>(null);
const recruitmentFormsLoading = ref(false);
let recruitmentFormsPromise: Promise<{ _id: string; display: string }[]> | null = null;

/**
 * Shared composable that returns the recruitment evaluation forms (moduleId: 4).
 * The API call is made only once and the result is cached at module level.
 * Every caller shares the same reactive `forms` ref and `isLoading` state.
 */
export const useRecruitmentEvaluationForms = () => {
  if (!recruitmentFormsPromise) {
    recruitmentFormsLoading.value = true;
    recruitmentFormsPromise = formularApi
      .getPaginated({ moduleId: 4, options: { paginated: false } })
      .promise.then((response) => {
        recruitmentFormsCache.value = response;
        return response;
      })
      .finally(() => {
        recruitmentFormsLoading.value = false;
      });
  }

  return {
    forms: recruitmentFormsCache,
    isLoading: recruitmentFormsLoading,
    promise: recruitmentFormsPromise,
  };
};

/**
 *
 * @deprecated
 */
export const useInfiniteFormular = ({
  values = [],
  limit = ref(30),
  page = ref(0),
  moduleId = 7,
}) => {
  // <b-autocomplete-infity class="w-full" @get-more-data="getMoreResources" :list="resourceAutoComplete.list"
  // :selected="project.contributors" @add-selected="addSalesContributor"
  // @remove-selected="removeSalesContributor"
  // :haveMore="resourceAutoComplete.haveMore && !resourceAutoComplete.loading">
  // <template #custom-list="{ selectItem, list }">
  const list = ref([]);
  const haveMore = ref(true);
  const loading = ref(false);
  const select = (selected) => {
    const formularIndex = values.findIndex((formular) => formular._id === selected._id);
    if (formularIndex > -1) return values.splice(formularIndex, 1);
    values.push(selected);
  };
  const getMoreData = async (options) => {
    if (options.new) {
      page.value = 0;
      haveMore.value = true;
    }
    loading.value = true;
    const formularRequest = await formularApi.getPaginated({
      moduleId,
      options: { page: page.value, limit: limit.value, search: options.search },
    }).promise;

    loading.value = false;
    list.value = options.new ? formularRequest.docs : [...list.value, ...formularRequest.docs];
    haveMore.value = formularRequest.count?.currentPage < formularRequest.count?.totalPages;
    page.value++;
  };

  const remove = (toRemove) => {
    const foundIndex = values.findIndex((formular) => formular._id === toRemove._id);
    if (foundIndex === -1) return;

    values.splice(foundIndex, 1);
  };

  return {
    haveMore,
    getMoreData,
    remove,
    select,
    list,
    loading,
    values,
  };
};

export const useInfinityFormular = ({
  search = ref(""),
  limit = ref(20),
  page = ref(0),
  isLoading = ref(false),
  list = ref([]),
  moduleId = 7,
  extraQuery = {},
}: {
  search?: any;
  limit?: any;
  page?: any;
  isLoading?: any;
  list?: any;
  moduleId?: number;
  extraQuery?: MaybeRefOrGetter<Record<string, unknown>>;
} = {}) => {
  const haveMore = ref(true);
  const getMoreData = async () => {
    try {
      isLoading.value = true;
      const searchRequest = formularApi.getPaginated({
        options: {
          page: page.value,
          limit: limit.value,
          search: search.value,
          ...toValue(extraQuery),
        },
        moduleId,
      });
      const searchResponse = await searchRequest.promise;
      haveMore.value = searchResponse.count.hasNextPage;
      list.value = page.value === 0 ? searchResponse.docs : [...list.value, ...searchResponse.docs];
      return searchResponse;
    } catch (err) {
      console.log("err", err);
    } finally {
      isLoading.value = false;
    }
  };
  return {
    search,
    limit,
    page,
    haveMore,
    getMoreData,
    list,
    isLoading,
  };
};
